<template>
  <View
    :direction="inline ? 'row' : 'column'"
    align="center"
    :justify="inline ? 'start' : 'center'"
    gap="sm"
    :padding-block="inline ? undefined : 'lg'"
    class="ai-loading"
    :class="{ inline }"
  >
    <span class="ai-loading__icon" aria-hidden="true">
      <Icon name="sparkle" :size="inline ? 'extra-small' : 'medium'" />
    </span>
    <span class="ai-loading__label">
      <Transition name="ai-loading-label">
        <Text :key="label" variant="body-medium" color="secondary">{{ label }}</Text>
      </Transition>
    </span>
  </View>
</template>

<script setup lang="ts">
import Icon from './icon.vue';
import Text from './text.vue';
import View from './view/view.vue';

withDefaults(
  defineProps<{
    label?: string;
    /** A one-line status, icon beside the label, for a wait shown inside a field or a row */
    inline?: boolean;
  }>(),
  { label: 'AI is analyzing…', inline: false },
);
</script>

<style scoped lang="scss">
.ai-loading {
  width: 100%;
}

.ai-loading__icon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--t-border-radius-sm);
  background-color: var(--t-color-orange-100);
  color: var(--t-color-orange-500);
  // Gentle pulse signals active work without a spinner's "stuck" feel.
  animation: ai-loading-pulse 1.4s ease-in-out infinite;

  .inline & {
    width: 1.75rem;
    height: 1.75rem;
  }
}

// A changed label cross-fades in place: both labels share one cell while they swap, so nothing moves
.ai-loading__label {
  display: grid;

  > * {
    grid-area: 1 / 1;
  }
}

.ai-loading-label-enter-active,
.ai-loading-label-leave-active {
  transition: opacity 200ms var(--t-ease-out);
}

.ai-loading-label-enter-from,
.ai-loading-label-leave-to {
  opacity: 0;
}

@keyframes ai-loading-pulse {
  0%,
  100% {
    opacity: 0.55;
    transform: scale(0.94);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .ai-loading__icon {
    animation: none;
  }
}
</style>
